import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ArmadaNav } from "@/components/ArmadaNav";
import { TESTID } from "@/lib/testIds";
import { Activity, CheckCircle2, AlertCircle, RefreshCw } from "lucide-react";

const EVENT_LABEL = {
  login_success: { label: "Login OK", color: "emerald" },
  login_failed: { label: "Login Failed", color: "red" },
  magic_link_sent: { label: "Magic Sent", color: "amber" },
  magic_link_verified_mfa_pending: { label: "Magic Verified · MFA Pending", color: "amber" },
  magic_link_sent_by_admin: { label: "Sovereign Magic", color: "amber" },
  mfa_verified: { label: "MFA OK", color: "emerald" },
  mfa_failed: { label: "MFA Failed", color: "red" },
  password_reset_requested: { label: "Reset Requested", color: "amber" },
  password_reset_complete: { label: "Password Reset", color: "emerald" },
  password_force_reset: { label: "Force Reset (Sovereign)", color: "amber" },
  logout: { label: "Logout", color: "zinc" },
};

export default function SignalsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/login-events?limit=200");
      setEvents(data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = filter
    ? events.filter((e) =>
        e.email?.toLowerCase().includes(filter.toLowerCase()) ||
        e.event_type?.toLowerCase().includes(filter.toLowerCase())
      )
    : events;

  return (
    <div className="min-h-screen bg-armada-base">
      <ArmadaNav />
      <main className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10">
        <div className="flex items-end justify-between mb-8 animate-fade-up">
          <div>
            <div className="text-[10px] tracking-armada text-[#D4AF37] font-bold mb-2">LOGIN SIGNALS</div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-white">Activity Monitor</h1>
            <p className="text-sm text-zinc-400 mt-2 font-light">
              Real-time intelligence on Armada authentication events.
            </p>
          </div>
          <button onClick={load} className="btn-ghost-gold flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        <div className="glass-strong rounded-xl p-4 mb-4 animate-fade-up stagger-1">
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by email or event type…"
            className="armada-input"
          />
        </div>

        <div className="glass-strong rounded-xl overflow-hidden animate-fade-up stagger-2">
          <table data-testid={TESTID.signalsTable} className="w-full">
            <thead>
              <tr className="text-left text-[10px] tracking-armada text-zinc-500 font-bold border-b border-white/5">
                <th className="px-5 py-3 w-10"></th>
                <th className="px-5 py-3">Email</th>
                <th className="px-5 py-3">Event</th>
                <th className="px-5 py-3">Hull</th>
                <th className="px-5 py-3">IP</th>
                <th className="px-5 py-3">Time</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-zinc-500 text-sm">Loading signals…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-zinc-500 text-sm">No signals recorded.</td></tr>
              )}
              {filtered.map((ev) => {
                const meta = EVENT_LABEL[ev.event_type] || { label: ev.event_type, color: "zinc" };
                const dotColor = {
                  emerald: "bg-emerald-400", red: "bg-red-400",
                  amber: "bg-amber-400", zinc: "bg-zinc-500",
                }[meta.color];
                return (
                  <tr key={ev.id} data-testid={TESTID.signalsRow} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                    <td className="px-5 py-3">
                      {ev.success ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-400" />
                      )}
                    </td>
                    <td className="px-5 py-3 text-sm text-white font-mono">{ev.email}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold tracking-wider uppercase`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
                        {meta.label}
                      </span>
                      {ev.message && (
                        <div className="text-[10px] text-zinc-500 mt-0.5 truncate max-w-[200px]">{ev.message}</div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-xs text-zinc-400">{ev.hull || "—"}</td>
                    <td className="px-5 py-3 text-xs text-zinc-400 font-mono">{ev.ip_address || "—"}</td>
                    <td className="px-5 py-3 text-xs text-zinc-500 font-mono">
                      {new Date(ev.created_at).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
